import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Printer, FileText, Package, CheckCircle, XCircle, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import PrintDialog from '@/components/PrintDialog';
import * as printerApi from '@/services/printerApi';
import { useUSNReplacement } from '@/hooks/useUSNReplacement';

const USNReprint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openReplaceDialog, replaceBatch } = useUSNReplacement();
  // Preset USN from deep-linking (e.g., Inhouse USN Status Report → Reprint)
  const presetUsn = location.state?.presetUsn || '';
  
  // Manual Entry Tab State
  const [manualUsnInput, setManualUsnInput] = useState('');
  const [manualUsnList, setManualUsnList] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const manualInputRef = useRef(null);
  
  // Document/Bundle Selection Tab State
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState('');
  const [bundles, setBundles] = useState([]);
  const [selectedBundle, setSelectedBundle] = useState('');
  const [bundleUsns, setBundleUsns] = useState([]);
  const [selectedUsns, setSelectedUsns] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [isLoadingUsns, setIsLoadingUsns] = useState(false);
  
  // Print Dialog State
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCodes, setPrintCodes] = useState([]);
  const [printSortNos, setPrintSortNos] = useState([]);

  // Bulk Reprint Progress Dialog State (Phase J — streaming progress).
  const [progressOpen, setProgressOpen] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0, current: '', failures: 0 });

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Auto-validate preset USN (deep-link from Inhouse USN Status Report)
  useEffect(() => {
    if (!presetUsn) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.post('/sections/fob/reprint/validate-usn', null, {
          params: { usn_code: String(presetUsn).trim() }
        });
        if (cancelled) return;
        setManualUsnList((prev) => {
          if (prev.find(item => item.usn_code === response.data.usn_code)) return prev;
          return [...prev, response.data];
        });
        toast.success(`USN ${response.data.usn_code} pre-loaded — click "Print All" to reprint.`);
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || `Could not pre-load USN ${presetUsn}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetUsn]);

  // Clear print data when dialog closes
  useEffect(() => {
    if (!showPrintDialog) {
      setPrintCodes([]);
      setPrintSortNos([]);
    }
  }, [showPrintDialog]);

  const fetchDocuments = async () => {
    setIsLoadingDocuments(true);
    try {
      const response = await apiClient.get('/sections/fob/reprint/documents');
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDocumentChange = async (entryId) => {
    setSelectedDocument(entryId);
    setSelectedBundle('');
    setBundleUsns([]);
    setSelectedUsns([]);
    
    if (!entryId) {
      setBundles([]);
      return;
    }
    
    setIsLoadingBundles(true);
    try {
      const response = await apiClient.get(`/sections/fob/reprint/bundles/${entryId}`);
      setBundles(response.data.bundles || []);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundles');
    } finally {
      setIsLoadingBundles(false);
    }
  };

  const handleBundleChange = async (bundleBarcode) => {
    setSelectedBundle(bundleBarcode);
    setBundleUsns([]);
    setSelectedUsns([]);
    
    if (!bundleBarcode || !selectedDocument) return;
    
    setIsLoadingUsns(true);
    try {
      // Use query parameter for bundle barcode to avoid URL encoding issues
      const response = await apiClient.get(`/sections/fob/reprint/usns/${selectedDocument}`, {
        params: { bundle_barcode: bundleBarcode }
      });
      setBundleUsns(response.data.usns || []);
    } catch (error) {
      console.error('Error fetching USNs:', error);
      toast.error('Failed to load USNs');
    } finally {
      setIsLoadingUsns(false);
    }
  };

  // Manual Entry Functions
  const handleAddManualUsn = async (e) => {
    e.preventDefault();
    
    const usn = manualUsnInput.trim();
    if (!usn) {
      toast.error('Please enter USN code');
      return;
    }
    
    // Check if already in list
    if (manualUsnList.find(item => item.usn_code === usn)) {
      toast.error('USN already in list');
      setManualUsnInput('');
      manualInputRef.current?.focus();
      return;
    }
    
    setIsValidating(true);
    try {
      const response = await apiClient.post('/sections/fob/reprint/validate-usn', null, {
        params: { usn_code: usn }
      });
      
      setManualUsnList(prev => [...prev, response.data]);
      toast.success('USN added to print list');
      setManualUsnInput('');
      manualInputRef.current?.focus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid USN';
      toast.error(errorMsg);
      setManualUsnInput('');
      manualInputRef.current?.focus();
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveManualUsn = (usnCode) => {
    setManualUsnList(prev => prev.filter(item => item.usn_code !== usnCode));
    toast.success('USN removed from list');
  };

  const handlePrintManualUsns = async () => {
    if (manualUsnList.length === 0) {
      toast.error('No USNs to print');
      return;
    }
    const codes = manualUsnList.map(item => item.usn_code);
    const sortNos = manualUsnList.map(item => `Sort: ${item.sort_no}`);

    // Replace each USN before printing
    const dialog = await openReplaceDialog({
      count: codes.length,
      sourceFlow: 'fob_reprint',
      subjectLabel: `${codes.length} FOB USN(s)`,
    });
    if (!dialog) return;
    const batch = await replaceBatch(codes, {
      reason: dialog.reason,
      sourceFlow: 'fob_reprint',
      linkedDocuments: { source_page: 'FOB:USNReprint:Manual' },
      onProgress: (ev) => {
        if (ev.type === 'start') {
          setProgressOpen(true);
          setProgress({ completed: 0, total: ev.total, current: '', failures: 0 });
        } else if (ev.type === 'progress') {
          setProgress((prev) => ({
            completed: ev.completed,
            total: ev.total,
            current: ev.old_usn || '',
            failures: prev.failures + (ev.ok ? 0 : 1),
          }));
        }
      },
    });
    setProgressOpen(false);
    if (!batch) return;
    if (batch.failed_count) {
      const firstDetail = batch.failures?.[0]?.detail || 'unknown reason';
      toast.warning(
        batch.failed_count === 1
          ? `Replace failed: ${firstDetail}`
          : `${batch.failed_count} replace failures — first: ${firstDetail}`,
        { duration: 10000 }
      );
      console.warn('USN replace failures:', batch.failures);
    }
    const oldToNew = Object.fromEntries(batch.replacements.map(r => [r.old_usn, r.new_usn]));
    const newCodes = codes.map(c => oldToNew[c] || c);

    setPrintCodes(newCodes);
    setPrintSortNos(sortNos);
    setShowPrintDialog(true);
  };

  // Bundle Selection Functions
  const handleToggleUsn = (usnCode) => {
    setSelectedUsns(prev => {
      if (prev.includes(usnCode)) {
        return prev.filter(code => code !== usnCode);
      } else {
        return [...prev, usnCode];
      }
    });
  };

  const handleSelectAllNotAssorted = () => {
    const notAssortedUsns = bundleUsns.filter(usn => !usn.is_assorted).map(usn => usn.usn_code);
    setSelectedUsns(notAssortedUsns);
    toast.success(`Selected ${notAssortedUsns.length} unassorted USNs`);
  };

  const handleSelectAll = () => {
    const allUsns = bundleUsns.map(usn => usn.usn_code);
    setSelectedUsns(allUsns);
    toast.success(`Selected all ${allUsns.length} USNs`);
  };

  const handleClearSelection = () => {
    setSelectedUsns([]);
    toast.success('Selection cleared');
  };

  const handlePrintSelectedUsns = async () => {
    if (selectedUsns.length === 0) {
      toast.error('No USNs selected');
      return;
    }
    const selectedUsnData = bundleUsns.filter(usn => selectedUsns.includes(usn.usn_code));
    const codes = selectedUsnData.map(usn => usn.usn_code);
    const sortNos = selectedUsnData.map(usn => `Sort: ${usn.sort_no}`);

    const dialog = await openReplaceDialog({
      count: codes.length,
      sourceFlow: 'fob_reprint',
      subjectLabel: `${codes.length} FOB USN(s) from bundle ${selectedDocument}`,
    });
    if (!dialog) return;
    const batch = await replaceBatch(codes, {
      reason: dialog.reason,
      sourceFlow: 'fob_reprint',
      linkedDocuments: { source_page: 'FOB:USNReprint:Bundle', document_id: selectedDocument },
      onProgress: (ev) => {
        if (ev.type === 'start') {
          setProgressOpen(true);
          setProgress({ completed: 0, total: ev.total, current: '', failures: 0 });
        } else if (ev.type === 'progress') {
          setProgress((prev) => ({
            completed: ev.completed,
            total: ev.total,
            current: ev.old_usn || '',
            failures: prev.failures + (ev.ok ? 0 : 1),
          }));
        }
      },
    });
    setProgressOpen(false);
    if (!batch) return;
    if (batch.failed_count) {
      const firstDetail = batch.failures?.[0]?.detail || 'unknown reason';
      toast.warning(
        batch.failed_count === 1
          ? `Replace failed: ${firstDetail}`
          : `${batch.failed_count} replace failures — first: ${firstDetail}`,
        { duration: 10000 }
      );
      console.warn('USN replace failures:', batch.failures);
    }
    const oldToNew = Object.fromEntries(batch.replacements.map(r => [r.old_usn, r.new_usn]));
    const newCodes = codes.map(c => oldToNew[c] || c);

    setPrintCodes(newCodes);
    setPrintSortNos(sortNos);
    setShowPrintDialog(true);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/fob')}
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">USN Reprint</h1>
              <p className="text-sm text-[#E8DCC8]">Reprint damaged or missing USN stickers</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs defaultValue="manual" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            <TabsTrigger value="bundle">Document/Bundle</TabsTrigger>
          </TabsList>

          {/* Tab 1: Manual Entry */}
          <TabsContent value="manual" className="space-y-6">
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plus className="w-5 h-5 text-[#2D5F4F]" />
                  Add USN Manually
                </CardTitle>
                <CardDescription>
                  Enter USN codes one by one to create a print list
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddManualUsn} className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      ref={manualInputRef}
                      value={manualUsnInput}
                      onChange={(e) => setManualUsnInput(e.target.value)}
                      placeholder="Scan or enter USN code..."
                      className="text-lg h-12 border-2 border-[#E5DFD0] focus:border-[#2D5F4F]"
                      disabled={isValidating}
                      autoComplete="off"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="h-12 px-6 bg-[#2D5F4F] hover:bg-[#234A3D]"
                    disabled={isValidating}
                  >
                    {isValidating ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-5 h-5 mr-2" />
                        Add
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Manual USN List */}
            {manualUsnList.length > 0 && (
              <Card className="border-2 border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Print List</CardTitle>
                      <CardDescription>{manualUsnList.length} USN(s) ready to print</CardDescription>
                    </div>
                    <Button
                      onClick={handlePrintManualUsns}
                      className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print All ({manualUsnList.length})
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {manualUsnList.map((item, index) => (
                      <div 
                        key={item.usn_code} 
                        className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-[#5A5F6E] w-8">#{index + 1}</span>
                          <div>
                            <p className="font-mono font-semibold text-[#1A1F2E]">{item.usn_code}</p>
                            <div className="flex items-center gap-3 text-xs text-[#5A5F6E] mt-1">
                              <span>Sort: {item.sort_no}</span>
                              {item.is_assorted && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-3 h-3" />
                                  Assorted
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveManualUsn(item.usn_code)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {manualUsnList.length === 0 && (
              <Card className="border-2 border-dashed border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <FileText className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
                  <p className="text-[#5A5F6E]">No USNs added yet</p>
                  <p className="text-sm text-[#5A5F6E]/70 mt-1">Scan or enter USN codes above to create print list</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab 2: Document/Bundle Selection */}
          <TabsContent value="bundle" className="space-y-6">
            <Card className="border-2 border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-[#2D5F4F]" />
                  Select Document & Bundle
                </CardTitle>
                <CardDescription>
                  Choose a document and bundle to view all USNs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Document Selector */}
                <div className="space-y-2">
                  <Label>Document Number</Label>
                  <Select value={selectedDocument} onValueChange={handleDocumentChange}>
                    <SelectTrigger className="h-12 border-2 border-[#E5DFD0]">
                      <SelectValue placeholder="Select a document..." />
                    </SelectTrigger>
                    <SelectContent>
                      {documents.map((doc) => (
                        <SelectItem key={doc.entry_id} value={doc.entry_id}>
                          {doc.document_barcode} ({doc.total_usns} USNs)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bundle Selector */}
                {selectedDocument && (
                  <div className="space-y-2">
                    <Label>Bundle Barcode</Label>
                    <Select value={selectedBundle} onValueChange={handleBundleChange} disabled={isLoadingBundles}>
                      <SelectTrigger className="h-12 border-2 border-[#E5DFD0]">
                        <SelectValue placeholder="Select a bundle..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bundles.map((bundle) => (
                          <SelectItem key={bundle.bundle_barcode} value={bundle.bundle_barcode}>
                            {bundle.bundle_barcode} ({bundle.usn_count} USNs)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* USN List for Selected Bundle */}
            {bundleUsns.length > 0 && (
              <Card className="border-2 border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">USN List</CardTitle>
                      <CardDescription>
                        {selectedUsns.length} of {bundleUsns.length} selected
                        {' · '}
                        <span className="text-green-600">{bundleUsns.filter(u => u.is_assorted).length} assorted</span>
                        {' · '}
                        <span className="text-orange-600">{bundleUsns.filter(u => !u.is_assorted).length} not assorted</span>
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllNotAssorted}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Not Assorted Only
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearSelection}
                      >
                        Clear
                      </Button>
                      <Button
                        onClick={handlePrintSelectedUsns}
                        disabled={selectedUsns.length === 0}
                        className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Print ({selectedUsns.length})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {bundleUsns.map((usn) => (
                      <div 
                        key={usn.usn_code}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          selectedUsns.includes(usn.usn_code)
                            ? 'border-[#2D5F4F] bg-[#2D5F4F]/5'
                            : 'border-transparent bg-[#F5F1E8]'
                        }`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <Checkbox
                            checked={selectedUsns.includes(usn.usn_code)}
                            onCheckedChange={() => handleToggleUsn(usn.usn_code)}
                          />
                          <div className="flex-1">
                            <p className="font-mono font-semibold text-[#1A1F2E]">{usn.usn_code}</p>
                            <div className="flex items-center gap-3 text-xs text-[#5A5F6E] mt-1">
                              <span>Sort: {usn.sort_no}</span>
                              {usn.is_assorted ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-3 h-3" />
                                  Assorted by {usn.assorted_by}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-orange-600">
                                  <XCircle className="w-3 h-3" />
                                  Not Assorted
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedDocument && selectedBundle && bundleUsns.length === 0 && !isLoadingUsns && (
              <Card className="border-2 border-dashed border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <Package className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
                  <p className="text-[#5A5F6E]">No USNs found for this bundle</p>
                </CardContent>
              </Card>
            )}

            {!selectedDocument && (
              <Card className="border-2 border-dashed border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <FileText className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
                  <p className="text-[#5A5F6E]">Select a document to view bundles</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Print Dialog */}
      <PrintDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        codes={printCodes}
        sortNos={printSortNos}
        labelType="usn"
        defaultSection="FOB"
        operationKey="fob-usn-reprint"
        title="Print USN Stickers"
      />

      {/* Phase J — Bulk Reprint streaming progress dialog. Renders only
          while a batch >5 USNs is in flight on the streaming endpoint. */}
      {progressOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          data-testid="bulk-reprint-progress-overlay"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Replacing USNs…</h3>
              <span
                className="text-sm font-mono text-stone-600"
                data-testid="bulk-reprint-progress-counter"
              >
                {progress.completed} / {progress.total}
              </span>
            </div>
            <div
              className="w-full h-3 bg-stone-200 rounded-full overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total || 1}
              aria-valuenow={progress.completed}
            >
              <div
                className="h-full bg-emerald-600 transition-all duration-150 ease-out"
                style={{
                  width: progress.total
                    ? `${(progress.completed / progress.total) * 100}%`
                    : '0%',
                }}
                data-testid="bulk-reprint-progress-bar"
              />
            </div>
            <div className="text-xs text-stone-600 flex items-center justify-between">
              <span data-testid="bulk-reprint-current-usn">
                {progress.current
                  ? <>Now: <code className="font-mono">{progress.current}</code></>
                  : 'Starting…'}
              </span>
              {progress.failures > 0 && (
                <span
                  className="text-rose-600 font-medium"
                  data-testid="bulk-reprint-failures"
                >
                  {progress.failures} failure{progress.failures === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500 leading-relaxed">
              Each USN is being deactivated, replaced from the REPLACE pool,
              and cross-references rewritten across 30+ collections. This
              dialog auto-closes when all USNs have been processed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default USNReprint;
